import { keyframes, styled } from '@/styles/stitches.config';
import { PropsWithChildren, useContext } from 'react';
import { getThemeVariants, ThemeContext, Themes } from '../microstory/theme';

/**
 * For heading texts we animate the different wide parts of the text
 * This component wraps the consecutive wide words that appear on one line and animates them on mount
 */
export const AnimatedHeadingType = (
  props: PropsWithChildren<{
    index?: number;
    beforeNewLine?: boolean;
    afterNewLine?: boolean;
  }>
) => {
  const { children, index = 0 } = props;
  const startDelay = 0.5 + index * 0.22;
  const theme = useContext(ThemeContext);

  return (
    <MotionSpan
      theme={theme}
      css={{
        animationDelay: startDelay + 0.35 + 's',
      }}
    >
      {children}
    </MotionSpan>
  );
};

const stretch = keyframes({
  '0%': {
    fontVariationSettings: '"XTRA" 468, "opsz" 16',
    fontStretch: '100%',
  },
  '60%': {
    fontVariationSettings: '"XTRA" 600, "opsz" 16',
    fontStretch: '150%',
  },
  '100%': {
    fontVariationSettings: '"XTRA" 468, "opsz" 16',
    fontStretch: '100%',
  },
});

const stretchNoto = keyframes({
  '0%': {
    fontStretch: '100%',
  },
  '60%': {
    fontStretch: '62.5%',
  },
  '100%': {
    fontStretch: '100%',
  },
});

const stretchAlternate = keyframes({
  '0%': {
    transform: 'scale(1)',
  },
  '60%': {
    transform: 'scale(1.125)',
  },
  '100%': {
    transform: 'scale(1)',
  },
});

const MotionSpan = styled('span', {
  '--roboto-flex-xtra': 468,
  display: 'inline-block',
  paddingBlockStart: '0.14em',
  paddingBlockEnd: '0.28em',
  paddingInline: '0.5em',
  fontStretch: '100%',
  fontWeight: '$semibold',
  lineHeight: '$1',
  whiteSpace: 'nowrap',
  borderRadius: '1em',
  boxDecorationBreak: 'clone',
  '-webkit-box-decoration-break': 'clone',
  backgroundColor: '$unicef',
  color: '$white',
  animationName: stretch.name,
  animationDuration: '1.7s',
  animationTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  animationPlayState: 'running',
  animationIterationCount: 1,
  variants: {
    theme: getThemeVariants({
      [Themes.sky]: {
        backgroundColor: 'var(--color-tertiary)',
      },
    }),
  },
  '[lang=bn] &, [lang=ta] &': {
    animationName: stretchNoto.name,
  },
  '[lang=bn] &, [lang=hi] &, [lang=ta] &': {
    padding: '0.28em 0.5em 0.14em',
  },
  '[lang=hi] &, [lang=ar] &': {
    animationName: stretchAlternate.name,
    animationDuration: '1.2s',
  },
});
